import type { Firestore, Timestamp } from 'firebase-admin/firestore'
import { isString, isNonEmptyString } from '../../common/utils/type-guards.js'
import type {
  Clan,
  LiveSocialResponse,
  LiveSocialUser,
  LiveEntityKind,
  LiveLocationSnapshot,
  LiveUserStatus,
  TransportPreference,
  UserLocationSummary,
  ProfileIconColor
} from '@my-roadtrip/types'
import { createLogger } from '../../common/utils/logger.js'
import { UsersRepository } from '../users/users.repository.js'
import { ClanRepository } from '../clan/clan.repository.js'
import type { Follow } from '../social/social.types.js'

const REFRESH_INTERVAL_MS = 15_000
const ONLINE_THRESHOLD_MS = 2 * 60 * 1000
const IDLE_THRESHOLD_MS = 10 * 60 * 1000
const STALE_LOCATION_THRESHOLD_MS = 15 * 60 * 1000

interface BasicUserProfile {
  id: string
  displayName: string
  photoURL?: string | null
  iconSet?: { activeIcon?: string; accentColor?: string }
  preferences?: {
    privacy?: {
      allowLocationTracking?: boolean
      showLocation?: boolean
      shareLiveWithFriends?: boolean
      shareLiveWithClans?: boolean
    }
    travel?: {
      defaultTransport?: TransportPreference
    }
  }
  lastActive?: Date | string | Timestamp | null
}

interface BuildContext {
  kind: LiveEntityKind
  clan?: Clan | null
  requesterId: string
}

export class LiveServiceError extends Error {
  constructor(message: string, public readonly statusCode = 500) {
    super(message)
  }
}

export class LiveService {
  private readonly usersRepository: UsersRepository
  private readonly clanRepository: ClanRepository
  private readonly logger = createLogger('LiveService')

  constructor(private readonly firestore: Firestore) {
    this.usersRepository = new UsersRepository(firestore)
    this.clanRepository = new ClanRepository(firestore)
  }

  getRefreshInterval(): number {
    return REFRESH_INTERVAL_MS
  }

  async getFriendLocations(userId: string): Promise<LiveSocialResponse> {
    const friendIds = await this.resolveFriendIds(userId)
    const users = await this.buildLiveUsers(Array.from(friendIds), {
      kind: 'friend',
      requesterId: userId
    })

    return this.buildResponse(users)
  }

  async getClanLocations(userId: string, clanId: string): Promise<LiveSocialResponse> {
    const clan = await this.clanRepository.getClan(clanId)
    if (!clan) {
      throw new LiveServiceError('Clan not found', 404)
    }

    const isMember = clan.members.some(member => member.userId === userId)
    if (!isMember) {
      throw new LiveServiceError('Not a clan member', 403)
    }

    const users = await this.buildLiveUsers(
      clan.members.map(member => member.userId),
      {
        kind: 'clan',
        clan,
        requesterId: userId
      }
    )

    return this.buildResponse(users)
  }

  private buildResponse(users: LiveSocialUser[]): LiveSocialResponse {
    const sortOrder: Record<LiveUserStatus, number> = {
      online: 0,
      idle: 1,
      offline: 2
    }

    const sorted = [...users].sort((a, b) => {
      const statusDiff = sortOrder[a.status] - sortOrder[b.status]
      if (statusDiff !== 0) {
        return statusDiff
      }
      return a.displayName.localeCompare(b.displayName)
    })

    return {
      generatedAt: new Date().toISOString(),
      refreshIntervalMs: REFRESH_INTERVAL_MS,
      users: sorted
    }
  }

  private async resolveFriendIds(userId: string): Promise<Set<string>> {
    const [followingSnap, followersSnap] = await Promise.all([
      this.firestore
        .collection('follows')
        .where('followerId', '==', userId)
        .limit(200)
        .get(),
      this.firestore
        .collection('follows')
        .where('followingId', '==', userId)
        .limit(200)
        .get()
    ])

    const following = new Set<string>()
    followingSnap.forEach(doc => {
      const data = doc.data() as Follow
      if (data.followingId && data.followingId !== userId) {
        following.add(data.followingId)
      }
    })

    const mutual = new Set<string>()
    followersSnap.forEach(doc => {
      const data = doc.data() as Follow
      if (data.followerId && following.has(data.followerId) && data.followerId !== userId) {
        mutual.add(data.followerId)
      }
    })

    return mutual
  }

  private async buildLiveUsers(userIds: string[], context: BuildContext): Promise<LiveSocialUser[]> {
    if (!userIds.length) {
      return []
    }

    const uniqueIds = Array.from(
      new Set(userIds.filter(id => id && id !== context.requesterId))
    )

    if (!uniqueIds.length) {
      return []
    }

    const [profiles, summaries] = await Promise.all([
      this.fetchBasicProfiles(uniqueIds, context.clan ?? null),
      this.fetchLocationSummaries(uniqueIds)
    ])

    const clanMemberMap = context.clan
      ? new Map(context.clan.members.map(member => [member.userId, member]))
      : null

    return uniqueIds.map((userId) => {
      const profile = profiles.get(userId)
      const summary = summaries.get(userId)
      const clanMember = clanMemberMap?.get(userId)
      const sharePreference = this.canShareLocation(profile, context.kind)
      const location = sharePreference ? this.toLocationSnapshot(summary) : null
      const status = sharePreference ? this.deriveStatus(summary) : 'offline'
      const shouldExposeLocation =
        Boolean(location) && (status === 'online' || status === 'idle')
      const profileIconColor = profile?.iconSet?.accentColor as ProfileIconColor | undefined

      return {
        userId,
        displayName: profile?.displayName ?? clanMember?.displayName ?? 'Membre',
        avatarUrl: profile?.photoURL ?? clanMember?.photoURL ?? null,
        profileIcon: profile?.iconSet?.activeIcon as any,
        profileIconColor,
        kind: context.kind,
        status: shouldExposeLocation ? status : 'offline',
        isSharingLocation: shouldExposeLocation,
        lastActiveAt: this.resolveLastActive(summary, profile?.lastActive),
        travelMode: profile?.preferences?.travel?.defaultTransport,
        location: shouldExposeLocation ? location ?? undefined : undefined,
        clanId: context.clan?.id,
        clanName: context.clan?.name,
        clanRole: clanMember?.role
      }
    })
  }

  private async fetchBasicProfiles(
    userIds: string[],
    clan: Clan | null
  ): Promise<Map<string, BasicUserProfile>> {
    const profiles = new Map<string, BasicUserProfile>()

    // Fixed: Properly await all profile fetches in parallel
    await Promise.all(
      userIds.map(async (userId) => {
        try {
          const snapshot = await this.firestore.collection('users').doc(userId).get()
          if (!snapshot.exists) {
            return
          }
          const data = snapshot.data() as BasicUserProfile | undefined
          if (!data) {
            return
          }
          profiles.set(userId, {
            id: userId,
            displayName: data.displayName ?? clan?.members.find(m => m.userId === userId)?.displayName ?? 'Utilisateur',
            photoURL: data.photoURL ?? null,
            iconSet: data.iconSet,
            preferences: data.preferences,
            lastActive: data.lastActive ?? null
          })
        } catch (error) {
          this.logger.warn('Failed to load basic profile', { userId, error })
        }
      })
    )

    return profiles
  }

  private async fetchLocationSummaries(userIds: string[]): Promise<Map<string, UserLocationSummary>> {
    const summaries = new Map<string, UserLocationSummary>()
    // Fixed: Properly await all location summary fetches in parallel
    await Promise.all(
      userIds.map(async (userId) => {
        try {
          const summary = await this.usersRepository.getLocationSummary(userId)
          if (summary) {
            summaries.set(userId, summary)
          }
        } catch (error) {
          this.logger.warn('Failed to load location summary', { userId, error })
        }
      })
    )
    return summaries
  }

  private canShareLocation(profile: BasicUserProfile | undefined, kind: LiveEntityKind): boolean {
    if (!profile) {
      return false
    }
    const privacy = profile.preferences?.privacy
    if (!privacy) {
      return false
    }
    if (!privacy.allowLocationTracking || !privacy.showLocation) {
      return false
    }
    if (kind === 'friend' && privacy.shareLiveWithFriends === false) {
      return false
    }
    if (kind === 'clan' && privacy.shareLiveWithClans === false) {
      return false
    }
    return true
  }

  private toLocationSnapshot(summary: UserLocationSummary | undefined): LiveLocationSnapshot | null {
    if (!summary?.lastPosition || !summary.lastUpdate) {
      return null
    }

    const updatedAt = Date.parse(summary.lastUpdate)
    if (!Number.isFinite(updatedAt)) {
      return null
    }
    if (Date.now() - updatedAt > STALE_LOCATION_THRESHOLD_MS) {
      return null
    }

    const speedValue =
      typeof summary.lastPosition.speed === 'number'
        ? Math.round(summary.lastPosition.speed * 3.6 * 10) / 10
        : null

    return {
      lat: summary.lastPosition.lat,
      lng: summary.lastPosition.lng,
      accuracy: summary.lastPosition.accuracy ?? undefined,
      heading: summary.lastPosition.heading ?? null,
      speedKmh: speedValue,
      updatedAt: summary.lastUpdate,
      source: summary.lastSource
    }
  }

  private deriveStatus(summary: UserLocationSummary | undefined): LiveUserStatus {
    if (!summary?.lastUpdate) {
      return 'offline'
    }
    const updatedAt = Date.parse(summary.lastUpdate)
    if (!Number.isFinite(updatedAt)) {
      return 'offline'
    }
    const delta = Date.now() - updatedAt
    if (delta <= ONLINE_THRESHOLD_MS) {
      return 'online'
    }
    if (delta <= IDLE_THRESHOLD_MS) {
      return 'idle'
    }
    return 'offline'
  }

  private resolveLastActive(
    summary: UserLocationSummary | undefined,
    fallback: BasicUserProfile['lastActive']
  ): string | undefined {
    if (summary?.lastUpdate) {
      return summary.lastUpdate
    }
    if (!fallback) {
      return undefined
    }
    if (isString(fallback)) {
      const date = new Date(fallback)
      return Number.isNaN(date.getTime()) ? undefined : date.toISOString()
    }
    if (fallback instanceof Date) {
      return fallback.toISOString()
    }
    if (fallback && typeof fallback === 'object' && 'toDate' in fallback && typeof fallback.toDate === 'function') {
      return fallback.toDate().toISOString()
    }
    return undefined
  }
}
